<template>
  <section :style="`background-image: url(${require('@/assets/images/top_background.jpg')})`">
    <div class="room-container">
      <b-container fluid>
        <b-row no-gutters>
          <b-col v-show="enabledWhiteboard" cols="12" md="10">
            <div class="whiteboard-wrapper">
              <whiteboard-control
                :isMobile="device.isMobile"
                :isSupportedDisplayMedia="device.isSupportedDisplayMedia"
                @onClickProjector="onClickProjector"
                @onClickImagePaste="onClickImagePaste"
                @onClickSnapshot="onClickSnapshot"
                @onClickClearWhiteboard="onClickClearWhiteboard"
              />
              <whiteboard
                :enabledWhiteboard="enabledWhiteboard"
                ref="whiteboard"
              />
            </div>
          </b-col>
          <b-col cols="12" :md="enabledWhiteboard ? '2' : '12'">
            <div :class="['peers-wrapper', { 'separate': enabledWhiteboard }]">
              <peer-view
                :myself="true"
                :mediaStream="myStream"
                :enabledMic="enabledMic"
                :enabledVideo="enabledVideo"
                @onClickMic="onClickMic"
                @onClickVideo="onClickVideo"
              />
              <peer-view
                v-for="peer in peers"
                :key="peer.id"
                :mediaStream="peer.mediaStream"
                :enabledMic="peer.enabledMic"
                :enabledVideo="peer.enabledVideo"
              />
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>
    <div class="room-control-container">
      <room-control
        :isMobile="device.isMobile"
        :isSupportedDisplayMedia="device.isSupportedDisplayMedia"
        :enabledMic="enabledMic"
        :enabledVideo="enabledVideo"
        :enabledWhiteboard="enabledWhiteboard"
        :enabledMinutes="enabledMinutes"
        :enabledRoomLock="enabledRoomLock"
        :projector="projector"
        @onClickMic="onClickMic"
        @onClickVideo="onClickVideo"
        @onClickProjector="onClickProjector"
        @onClickWhiteboard="onClickWhiteboard"
        @onClickMinutes="onClickMinutes"
        @onClickRoomLock="roomLockModal = !roomLockModal"
        @onClickExitRoom="onClickExitRoom"
      />
    </div>
    <minutes-modal
      :modal="enabledMinutes"
      :device="device"
      @onClickSendMinutesText="onClickSendMinutesText"
    />
    <alert-modal
      :modal="invalidRuleModal"
      :body="$t('common.feedbacks.roomName.invalidRule')"
      @toggleModal="(modal) => invalidRuleModal = modal"
      @alerted="() => this.$router.push({ name: 'top' })"
    />
    <alert-modal
      :modal="invalidRoomNameModal"
      :body="$t('common.feedbacks.roomName.invalidRoomName')"
      @toggleModal="(modal) => invalidRoomNameModal = modal"
      @alerted="() => this.$router.push({ name: 'top' })"
    />
    <alert-modal
      :modal="deviceSettingModal"
      @toggleModal="(modal) => deviceSettingModal = modal"
      @alerted="initPeerClient"
    >
      <label>カメラを選択してください</label>
      <b-form-select v-model="mediaVideoInputId" :options="mediaInputOptions('videoinput')" size="sm" class="mb-3"></b-form-select>
      <label>マイクを選択してください</label>
      <b-form-select v-model="mediaAudioInputId" :options="mediaInputOptions('audioinput')" size="sm" class="mb-3"></b-form-select>
    </alert-modal>
    <alert-modal
      :modal="requiredPasswordModal"
      @toggleModal="(modal) => requiredPasswordModal = modal"
      @alerted="peerClient.enterRoom(password)"
    >
      <div class="py-3 text-center">
        <p class="mb-3">{{ $t('pages.room.modal.requiredPasswordModalMessage') }}</p>
        <b-form-input type="password" v-model="password" autocomplete="new-password" :placeholder="$t('common.password')"></b-form-input>
      </div>
    </alert-modal>
    <alert-modal
      :modal="invalidUserMediaModal"
      :body="$t('pages.room.modal.invalidUserMediaModalMessage')"
      @toggleModal="(modal) => invalidUserMediaModal = modal"
      @alerted="() => this.$router.push({ name: 'top' })"
    />
    <alert-modal
      :modal="peerClientErrorModal"
      :body="peerClientErrorModalMessage"
      @toggleModal="(modal) => peerClientErrorModal = modal"
      @alerted="() => this.$router.push({ name: 'top' })"
    />
    <confirm-modal
      :modal="clearWhiteboardConfirmModal"
      :body="$t('pages.room.modal.clearWhiteboardConfirmModalMessage')"
      @toggleModal="(modal) => clearWhiteboardConfirmModal = modal"
      @confirmed="confirmedClearWhiteboard"
    />
    <confirm-modal
      :modal="exitConfirmModal"
      @toggleModal="(modal) => { exitConfirmModal = modal; requireDownloadMinutesData = false }"
      @confirmed="exitRoom"
    >
      <div class="py-3 text-center">
        <h5 class="mb-3">{{ $t('pages.room.modal.exitConfirmModalMessage') }}</h5>
        <b-form-checkbox v-if="minutesText" v-model="requireDownloadMinutesData" size="sm">
          {{ $t('pages.room.modal.downloadMinutesData') }}
        </b-form-checkbox>
      </div>
    </confirm-modal>
    <confirm-modal
      :modal="roomLockModal"
      :title="enabledRoomLock ? $t('pages.room.modal.reomveRoomLockPasswordTitle') : $t('pages.room.modal.setRoomLockPasswordTitle')"
      :disabledOkButton="!enabledRoomLock && (!passwordSettings.origin || passwordSettings.origin != passwordSettings.confirm)"
      @toggleModal="(modal) => roomLockModal = modal"
      @confirmed="confirmedRoomLock"
    >
      <div v-if="enabledRoomLock">
        <p class="mb-3">{{ $t('pages.room.modal.reomveRoomLockPasswordMessage') }}</p>
      </div>
      <div v-else>
        <div class="py-3 text-center">
          <b-form-input type="password" v-model="passwordSettings.origin" autocomplete="new-password" :placeholder="$t('common.password')"></b-form-input>
        </div>
        <div class="py-3 text-center">
          <b-form-input type="password" v-model="passwordSettings.confirm" autocomplete="new-password" :placeholder="$t('common.passwordConfirmation')"></b-form-input>
        </div>
      </div>
    </confirm-modal>
  </section>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import PeerClient from 'peerjs'
import deviceInfo from '@/lib/deviceInfo'
import PEER_CONFIG from '@/lib/peerConfig'
import { WHITEBOARD_BASE_SIZE, INVALID_ROOM_NAMES } from '@/lib/constants'

import Whiteboard from '@/components/organisms/room/Whiteboard'
import WhiteboardControl from '@/components/organisms/room/WhiteboardControl'
import MinutesModal from '@/components/organisms/room/MinutesModal'

import PeerView from '@/components/molecules/room/PeerView'
import RoomControl from '@/components/molecules/room/RoomControl'
import AlertModal from '@/components/molecules/room/AlertModal'
import ConfirmModal from '@/components/molecules/room/ConfirmModal'

export default {
  name: 'Room',
  components: {
    PeerView,
    Whiteboard,
    WhiteboardControl,
    MinutesModal,
    RoomControl,
    AlertModal,
    ConfirmModal
  },
  data () {
    return {
      closed: false,
      myPeerid: '',
      myStream: null,
      password: '',
      passwordSettings: {
        origin: '',
        condirm: ''
      },
      enabledMic: false,
      enabledVideo: false,
      enabledWhiteboard: false,
      enabledMinutes: false,
      enabledRoomLock: false,
      mediaDevices: [],
      mediaAudioInputId: null,
      mediaVideoInputId: null,
      peerClient: null,
      invalidRoomNameModal: false,
      invalidRuleModal: false,
      deviceSettingModal: false,
      requiredPasswordModal: false,
      invalidUserMediaModal: false,
      peerClientErrorModal: false,
      peerClientErrorModalMessage: this.$t('pages.room.modal.peerClientErrorModalMessages.default'),
      clearWhiteboardConfirmModal: false,
      exitConfirmModal: false,
      roomLockModal: false,
      requireDownloadMinutesData: false
    }
  },
  computed: {
    device: () => deviceInfo(),
    soundEnter: () => {
      let audio = new Audio()
      const canPlayMp3 = audio.canPlayType('audio/mp3')
      if (canPlayMp3 === 'probably' || canPlayMp3 === 'maybe') {
        audio = new Audio(require('@/assets/audios/knocking_door.mp3'))
      } else {
        audio = new Audio(require('@/assets/audios/knocking_door.ogg'))
      }
      audio.volume -= 0.5
      return audio
    },
    roomName: function() { return this.$route.params.roomName },
    mediaInputOptions: function() {
      return function(kind) {
        return this.mediaDevices.filter(device => {
          return device.deviceId !== 'default' && device.kind === kind
        }).map(device => {
          return {
            text: device.label ? device.label : '不明なデバイス',
            value: device.deviceId
          }
        })
      }
    },
    ...mapGetters({
      peers: 'peers/peers',
      aboriginalPeers: 'peers/aboriginalPeers',
      peerById: 'peers/peerById',
      removePeerId: 'peers/removePeerId',
      sendWhiteboardData: 'whiteboard/sendData',
      whiteboardImageData: 'whiteboard/sendImageData',
      snapshotImage: 'whiteboard/snapshotImage',
      projector: 'projector/projector',
      minutesText: 'minutes/text'
    })
  },
  methods: {
    validateRoomName: function() {
      this.invalidRuleModal = this.roomName.match(/^[a-zA-Z0-9-_]*$/) === null
      this.invalidRoomNameModal = INVALID_ROOM_NAMES.includes(this.roomName)

      return !this.invalidRuleModal && !this.invalidRoomNameModal
    },
    setDefaultDevices: function() {
      const defaultAudioDevice = this.mediaDevices.filter(device => {
        return device.deviceId !== 'default' && device.kind === 'audioinput'
      })[0]
      if (defaultAudioDevice) this.mediaAudioInputId = defaultAudioDevice.deviceId
      const defaultVideoDevice = this.mediaDevices.filter(device => {
        return device.deviceId !== 'default' && device.kind === 'videoinput'
      })[0]
      if (defaultVideoDevice) this.mediaVideoInputId = defaultVideoDevice.deviceId
    },
    initPeerClient: function() {
      const peerClient = new PeerClient({
        secure: PEER_CONFIG.secure,
        host: PEER_CONFIG.host,
        port: PEER_CONFIG.port,
        roomName: this.roomName,
        config: {
          iceServers: PEER_CONFIG.iceServers
        }
      })

      peerClient.on('connect', (_, requiredPassword) => {
        if (requiredPassword) {
          this.requiredPasswordModal = this.enabledRoomLock = true
        } else {
          peerClient.enterRoom()
        }
      })

      peerClient.on('open', async (id, peerIds) => {
        this.myPeerid = id
        await this.enableUserMedia()
        if (this.invalidUserMediaModal) return

        peerIds.filter(peerId => peerId !== id).forEach(peerId => {
          const call = peerClient.call(peerId, this.myStream)
          const conn = peerClient.connect(peerId)

          this.setPeer({ peerId: peerId, aboriginal: true })
          const peer = this.peerById(peerId)

          peer.setMediaCall(call)
          peer.setDataConnection(conn, () => {
            this.sendMyMediaState(conn)
          })
        })
        this.onClickVideo()
        this.onClickMic()

        peerClient.on('connection', (conn) => {
          this.soundEnter.play()
          const peerId = conn.peer

          let peer = this.peerById(peerId)
          if (!peer) {
            this.setPeer({ peerId: peerId })
            peer = this.peerById(peerId)
          }

          peer.setDataConnection(conn, () => {
            if (this.aboriginalPeers.length == 0) {
              this.setWhiteboardSendImageData()
              conn.send(this.whiteboardImageData)
              conn.send(JSON.stringify({
                type: 'minutes',
                text: this.minutesText
              }))
            }

            this.sendMyMediaState(conn)
          })
        })

        peerClient.on('call', (call) => {
          const peerId = call.peer

          if (call.metadata && call.metadata.display) {

            if (this.projector.displayCall) {
              this.removeProjectorDisplayCall()
            }

            call.answer(null)
            call.on('stream', (stream) => {
              this.enabledWhiteboard = true
              this.setProjectorStream({
                stream: stream,
                isStreamOwner: false
              })
            })

            call.on('close', () => {
              this.removeProjectorStream()
            })

            call.on('error', () => {
              this.removeProjectorStream()
            })

            this.setProjectorDisplayCall(call)

          } else {
            call.answer(this.myStream)

            let peer = this.peerById(peerId)
            if (!peer) {
              this.setPeer({ peerId: peerId })
              peer = this.peerById(peerId)
            }

            peer.setMediaCall(call)
            if (this.projector.isStreamOwner) {
              const displayCall = this.peerClient.call(peerId, this.projector.stream, { metadata: { display: true } })
              peer.setDisplayCall(displayCall)
            }
          }
        })

        peerClient.on('password-changed', (_, payload) => {
          const { remove } = payload
          this.enabledRoomLock = !remove
        })
      })

      peerClient.on('disconnected', () => {
        if (!this.closed) peerClient.reconnect()
      })

      peerClient.on('error', (e) => {
        console.error(e)

        switch (e.message) {
          case 'Server has reached its concurrent user limit': {
            this.peerClientErrorModalMessage = this.$t('pages.room.modal.peerClientErrorModalMessages.connectionLimitExceed')
            break
          }
          case 'Invalid room password': {
            this.peerClientErrorModalMessage = this.$t('pages.room.modal.peerClientErrorModalMessages.invalidRoomPassword')
            break
          }
        }

        if (!this.closed && e.type != 'webrtc') {
          this.close()

          // NOTE: リロード時にエラーモーダルを表示しないように100ms遅らせる
          setTimeout(() => {
            this.peerClientErrorModal = true
          }, 100)
        }
      })

      this.peerClient = peerClient
    },
    async enableUserMedia() {
      const mediaConstraints = {
        audio: {
          deviceId: this.mediaAudioInputId
        },
        video: {
          deviceId: this.mediaVideoInputId,
          frameRate: { max: 30 },
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      }
      try {
        this.myStream = await navigator.mediaDevices.getUserMedia(mediaConstraints)
        this.enabledMic = this.myStream.getAudioTracks()[0].enabled
        this.enabledVideo = this.myStream.getVideoTracks()[0].enabled
      } catch (e) {
        console.error(e)
        this.invalidUserMediaModal = true
      }
    },
    async enableDisplayMedia() {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          audio : false,
          video : {
            displaySurface: 'monitor',
            logicalSurface: true,
            cursor: true,
            width: { max: WHITEBOARD_BASE_SIZE.width },
            height: { max: WHITEBOARD_BASE_SIZE.height },
            frameRate: { max: 5 }
          }
        })
        this.setProjectorStream({
          stream: stream,
          isStreamOwner: true
        })
        this.peers.forEach(peer => {
          const displayCall = this.peerClient.call(peer.id, stream, { metadata: { display: true } })
          peer.setDisplayCall(displayCall)
        })
      } catch (e) {
        console.error(e)
      }
    },
    disableDisplayMedia: function() {
      if (!this.projector.stream || !this.projector.isStreamOwner) return

      this.peers.forEach(peer => {
        peer.removeDisplayCall()
      })
      this.removeProjectorStream()
    },
    confirmedClearWhiteboard: function() {
      this.clearWhiteboard()
      this.peers.forEach(peer => {
        peer.dataConnection.send(JSON.stringify({
          type: 'whiteboard',
          action: 'clear'
        }))
      })
    },
    confirmedRoomLock: function() {
      this.peerClient.setPassword(this.passwordSettings.origin)
      this.passwordSettings = {
        orgin: '',
        confirm: ''
      }
    },
    close: function() {
      if (this.closed) return

      this.closed = true
      this.disableDisplayMedia()

      // NOTE: Androidの場合にここの処理で固まってしまう
      // setTimeout(() => {}, 0)で処理分割することで解消
      setTimeout(() => {

        if (this.myStream) {
          this.myStream.getTracks().forEach(track => {
            track.stop()
          })
        }

        this.peers.forEach(peer => {
          peer.mediaCall.close()
          peer.dataConnection.close()
        })

        if (this.peerClient) this.peerClient.destroy()

        this.resetModules()
      }, 0)
    },
    sendMyMediaState: function(conn) {
      if (conn) {
        conn.send(JSON.stringify({
          type: 'mediaState',
          enabledMic: this.enabledMic,
          enabledVideo: this.enabledVideo
        }))
      } else {
        this.peers.forEach(peer => {
          peer.dataConnection.send(JSON.stringify({
            type: 'mediaState',
            enabledMic: this.enabledMic,
            enabledVideo: this.enabledVideo
          }))
        })
      }
    },
    exitRoom: function() {
      if (this.requireDownloadMinutesData) {
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF])
        const blob = new Blob([bom, this.minutesText], { type: 'application/octet-stream' })
        const virtualLink = document.createElement('a')
        virtualLink.href = URL.createObjectURL(blob)
        virtualLink.download = `${this.roomName}_minutes_data.txt`
        virtualLink.click()
      }
      this.$router.go(-1)

      // NOTE: target="_blank"対応
      this.$router.push({ name: 'top' })
    },
    onClickMic: function() {
      this.enabledMic = this.myStream.getAudioTracks()[0].enabled = !this.myStream.getAudioTracks()[0].enabled
      this.sendMyMediaState()
    },
    onClickVideo: function() {
      this.enabledVideo = this.myStream.getVideoTracks()[0].enabled = !this.myStream.getVideoTracks()[0].enabled
      this.sendMyMediaState()
    },
    onClickWhiteboard: function() {
      this.enabledWhiteboard = !this.enabledWhiteboard
    },
    onClickMinutes: function() {
      this.enabledMinutes = !this.enabledMinutes
    },
    async onClickProjector() {
      if (this.projector.stream && !this.projector.isStreamOwner) return

      if (this.projector.stream) {
        this.disableDisplayMedia()
      } else {
        this.enableDisplayMedia()
      }
    },
    onClickImagePaste: function() {
      const virtualInput = document.createElement('input')
      virtualInput.type = 'file'
      virtualInput.accept = '.jpg,.gif,.png,image/gif,image/jpeg,image/png'
      virtualInput.onchange = () => {
        const reader = new FileReader()
        reader.readAsDataURL(virtualInput.files[0])
        reader.onload = () => {
          const image = new Image()
          image.onload = () => {
            this.imagePasteWhiteboard(image)
            this.peers.forEach(peer => {
              peer.dataConnection.send(this.whiteboardImageData)
            })
          }
          image.src = reader.result
        }
      }
      virtualInput.click()
    },
    onClickSnapshot:function() {
      this.snapshotWhiteboard({
        whiteboardCanvas: this.$refs.whiteboard.$refs.drawingArea,
        projector: this.projector.stream ? this.$refs.whiteboard.$refs.projector : null
      })
      const virtualLink = document.createElement('a')
      virtualLink.href = this.snapshotImage
      virtualLink.download = `${this.roomName}_snapshot.png`
      virtualLink.click()
    },
    onClickClearWhiteboard: function() {
      this.clearWhiteboardConfirmModal = true
    },
    onClickSendMinutesText: function(text) {
      this.peers.forEach(peer => {
        peer.dataConnection.send(JSON.stringify({
          type: 'minutes',
          text: text
        }))
      })
      this.setMinutesText(text)
    },
    onClickExitRoom: function() {
      this.exitConfirmModal = true
    },
    ...mapActions({
      imagePasteWhiteboard: 'whiteboard/imagePaste',
      snapshotWhiteboard: 'whiteboard/snapshot',
      clearWhiteboard: 'whiteboard/clear',
      resetModules: 'resetModules'
    }),
    ...mapMutations({
      setPeer: 'peers/setPeer',
      setWhiteboardSendImageData: 'whiteboard/setSendImageData',
      setProjectorStream: 'projector/setStream',
      removeProjectorStream: 'projector/removeStream',
      setProjectorDisplayCall: 'projector/setDisplayCall',
      removeProjectorDisplayCall: 'projector/removeDisplayCall',
      setMinutesText: 'minutes/setText'
    })
  },
  async mounted () {
    if (this.validateRoomName()) {
      try {
        const streamForPermission = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        })
        streamForPermission.getTracks().forEach(track => {
          track.stop()
        })
      } catch (e) {
        console.error(e)
        this.invalidUserMediaModal = true
        return
      }
      this.mediaDevices = await navigator.mediaDevices.enumerateDevices()
      this.setDefaultDevices()
      this.deviceSettingModal = true
    }
  },
  beforeDestroy: function() {
    this.close()
  },
  watch: {
    sendWhiteboardData: function(data) {
      this.peers.forEach(peer => {
        peer.dataConnection.send(data)
      })
    },
    projector: function(data) {
      this.enabledWhiteboard = data.stream !== null
    },
    removePeerId: function(data) {
      // NOTE: 共有状態で共有者が退室した場合のフェールセーフ
      if (data == this.projector?.displayCall?.peer) {
        this.removeProjectorStream()
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/mediaquery";

section {
  width: 100%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 100vw rgba(0, 0, 0, 0.2);
}

.room-container {
  height: 100%;
  padding: 15px 15px 70px 15px;
  display: flex;
  align-items: center;
}

.peers-wrapper {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
}

.whiteboard-wrapper {
  margin-bottom: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column-reverse;

  @include mq(md) {
    align-items: stretch;
    flex-direction: row;
  }
}

.room-control-container {
  z-index: 1000;
  position: fixed;
  bottom: 10px;
  padding: 10px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
}
</style>
